import Box from 'components/shared/Box'
import Text from 'components/shared/Text'

import IconWithText from 'components/shared/IconWithText'
import Link from 'components/shared/Link'
import { ThankYouInfoBox, ThankYouQuestions, ThankYouSocialBox } from './styles'

import { FormattedMessage, useIntl } from 'react-intl'
import { useResolutions } from 'store/resolutions'
import { useApp } from 'store/steps'

import logoFull from 'assets/images/logos/logo-full.svg'
import iconSingleTicket from 'assets/icons/singleTicket.svg'
import { useEffect, useRef } from 'react'
import * as LINKS from 'constants/links'
import { pushDataLayer } from 'helpers/analytics'
import {
    OrderPresenter,
    useGetOrderAnalitycsQuery,
} from 'store/api-slice/hooks'
import facebookIcon from 'assets/icons/facebookIcon.svg'
import instagramIcon from 'assets/icons/instagramIcon.svg'
import youtubeIcon from 'assets/icons/youtubeIcon.svg'
import { ThankYouSocialButton } from './sections/ThankYouSocialButton'
import { ThankYouHungryExcitements } from './sections/ThankYouHungryExcitements'
import { ThankYouStayLonger } from './sections/ThankYouStayLonger'
import { ThankYouRelaxtation } from './sections/ThankYouRelaxtation'
import { ThankYouAttractions } from './sections/ThankYouAttractions'
import { ProcessingPage } from 'routes/PaymentConfirmation/ProcessingPage'

interface ThankYouPageProps {
    orderData: OrderPresenter
}

const ThankYouPage = ({ orderData }: ThankYouPageProps) => {
    const analyticsRef = useRef(false)

    const { formatMessage } = useIntl()
    const { language } = useApp()
    const { isTablet } = useResolutions()

    const { id: orderId, email: userEmail, cart } = orderData
    const { type: appType, creatorType: appMode, isShipping } = cart || {}

    const isSelfcare = appType === 'selfcare'
    const isUnlimited = appMode === 'card' || appMode === 'card-renew'
    const isVouchers = appMode === 'voucher'

    const { data: analyticsData, isLoading: isAnalyticsDataLoading } =
        useGetOrderAnalitycsQuery(
            {
                orderId: orderId || '',
                _locale: language,
            },
            { skip: isSelfcare || !orderId }
        )

    const cardDeliveryType = isShipping
        ? formatMessage({
              id: 'annual.thankyou_confirmation_delivery_postal',
              defaultMessage:
                  'Na podany adres e-mail wyślemy Ci informację o dacie wysyłki karty: ',
          })
        : formatMessage(
              {
                  id: 'annual.thankyou_confirmation_delivery_pickup',
                  defaultMessage:
                      'Aby odebrać swoją kartę Unlimited, odwiedź <strong>kasę nr 1</strong> w Parku. Przy odbiorze podaj <strong>numer faktury</strong>, którą wysłaliśmy na adres: ',
              },
              {
                  strong: (chunks: string[]) => (
                      <Text
                          variant="bodyStrong"
                          color="grayscale_900"
                          as="strong"
                      >
                          {chunks}
                      </Text>
                  ),
              }
          )

    const selectThankyouPage = () => {
        if (isSelfcare) {
            return formatMessage({
                id: 'selfcare.thankyou.your_changes_to_order_is_ready_heading',
                defaultMessage: 'Zmiany w Twoim zamówieniu gotowe!',
            })
        } else if (isUnlimited) {
            return formatMessage({
                id: 'order.thankyou.your_order_is_in_progress_heading_unlimited',
                defaultMessage: 'Twoje zamówienie jest w trakcie realizacji!',
            })
        } else {
            return formatMessage({
                id: 'order.thankyou.your_order_is_ready_heading',
                defaultMessage: 'Twoje zamówienie jest gotowe!',
            })
        }
        return ''
    }

    // < analytics
    useEffect(() => {
        if (!analyticsData) return

        if (
            isSelfcare ||
            !analyticsData?.confirmationPageEvent ||
            analyticsRef.current
        )
            return

        const { confirmationPageEvent } = analyticsData

        pushDataLayer({ ...confirmationPageEvent })
        analyticsRef.current = true
    }, [analyticsData])
    // analytics >

    return isAnalyticsDataLoading ? (
        <ProcessingPage shouldShowProcessing={false} />
    ) : (
        <Box align="center" justify="center" mt={[12, 20]} isFullWidth={true}>
            <a href={LINKS.HOME_LINK}>
                <img src={logoFull} alt="Park of Poland logo" />
            </a>
            <Text
                variant="heading2"
                color="secondary_500"
                mb={[32, 64]}
                mt={[32, 46]}
                align="center"
            >
                <FormattedMessage
                    id="order.thankyou.thanks_for_choosing_suntago_heading"
                    defaultMessage="Dziękujemy za wybór Suntago!"
                />
            </Text>
            <Box
                gap={[16, 32]}
                direction={['column', 'row']}
                isFullWidth={isTablet}
                align="stretch"
            >
                <ThankYouInfoBox
                    isFullWidth={isTablet}
                    pt={[24, 40]}
                    pb={[24, 40]}
                    pl={[16, 40]}
                    pr={[16, 40]}
                >
                    <IconWithText
                        isIconDisabled={!!isTablet}
                        fontVariant="heading3"
                        icon={iconSingleTicket}
                        iconAlt="single ticket icon"
                        textHighlighted={selectThankyouPage()}
                    />
                    <Text
                        variant="body"
                        color="grayscale_900"
                        pt={[12, 16]}
                        pl={[0, 56]}
                        mb={isUnlimited ? 0 : 8}
                    >
                        {isUnlimited ? (
                            cardDeliveryType
                        ) : (
                            <FormattedMessage
                                id="order.thankyou.confirmation_sent_message"
                                defaultMessage="Potwierdzenie wyślemy na adres:"
                            />
                        )}
                    </Text>
                    <Text
                        variant="bodyStrong"
                        color="secondary_500"
                        pl={[0, 56]}
                    >
                        {!!userEmail && decodeURIComponent(userEmail)}
                    </Text>
                </ThankYouInfoBox>
                <ThankYouInfoBox
                    isFullWidth={isTablet}
                    justify={['space-between', 'flex-start']}
                    pt={[24, 40]}
                    pb={[24, 40]}
                    pl={[16, 40]}
                    pr={[16, 40]}
                    gap={16}
                    direction="column"
                >
                    <Box
                        direction="row"
                        align="center"
                        justify="space-between"
                        isFullWidth
                    >
                        <ThankYouQuestions variant="heading3">
                            <FormattedMessage
                                id="order.thankyou.faq_questions_heading"
                                defaultMessage="Masz pytania?"
                            />
                        </ThankYouQuestions>
                        <ThankYouSocialBox direction="row" align="center">
                            <ThankYouSocialButton
                                src={facebookIcon}
                                alt="Facebook"
                                link={LINKS.FACEBOOK_LINK}
                            />
                            <ThankYouSocialButton
                                src={instagramIcon}
                                alt="Instagram"
                                link={LINKS.INSTAGRAM_LINK}
                            />
                            <ThankYouSocialButton
                                src={youtubeIcon}
                                alt="Youtube"
                                link={LINKS.YOUTUBE_LINK}
                            />
                        </ThankYouSocialBox>
                    </Box>
                    <Box
                        ml={0}
                        isFullWidth={isTablet}
                        align="flex-start"
                        direction="column"
                        gap={12}
                    >
                        <Link
                            variant="secondary"
                            path={LINKS.FAQ_LINK}
                            data-testid="page-thankyou-redirect-faq"
                            icon={null}
                        >
                            {formatMessage({
                                id: 'global.thankyou.faq',
                                defaultMessage: 'Zobacz FAQ',
                            })}
                        </Link>
                        {isVouchers && (
                            <Link
                                variant="secondary"
                                path={LINKS.HOWTO_GET_LINK}
                                icon={null}
                            >
                                {formatMessage({
                                    id: 'global.thankyou.vouchers_howtoget_link',
                                    defaultMessage: 'Jak dojechać do parku',
                                })}
                            </Link>
                        )}
                        {isUnlimited && (
                            <>
                                <Link
                                    variant="secondary"
                                    path={LINKS.BENEFITS_LINK}
                                    data-testid="page-thankyou-redirect-unlimited-benefits"
                                    icon={null}
                                >
                                    {formatMessage({
                                        id: 'global.thankyou.unlimited_card_benefits_link',
                                        defaultMessage: 'Poznaj korzyści karty',
                                    })}
                                </Link>
                                <Link
                                    variant="secondary"
                                    path={LINKS.CARD_REGULATIONS_LINK}
                                    data-testid="page-thankyou-redirect-unlimited-regulations"
                                    icon={null}
                                >
                                    {formatMessage({
                                        id: 'global.thankyou.unlimited_card_regulations_link',
                                        defaultMessage:
                                            'Sprawdź regulamin karty',
                                    })}
                                </Link>
                                <Link
                                    variant="secondary"
                                    path={LINKS.CONTACT_LINK}
                                    data-testid="page-thankyou-redirect-contact"
                                    icon={null}
                                >
                                    {formatMessage({
                                        id: 'global.btn.contact_us',
                                        defaultMessage: 'Skontaktuj się z nami',
                                    })}
                                </Link>
                            </>
                        )}
                    </Box>
                </ThankYouInfoBox>
            </Box>
            <Box gap={[40, 32]} pt={[32, 64]} direction={['column', 'row']}>
                {isUnlimited && <ThankYouAttractions />}
                {isVouchers && <ThankYouRelaxtation />}
                {!isUnlimited && !isVouchers && <ThankYouHungryExcitements />}
                <ThankYouStayLonger
                    withLineBreak={isUnlimited && language === 'uk'}
                />
            </Box>
        </Box>
    )
}

export default ThankYouPage
